import { useTranslation } from "react-i18next";
import { LANGUAGES, type Lang } from "@/app/i18n/types";

const LOCALE_BY_LANG: Record<Lang, string> = {
  vi: "vi-VN",
  en: "en-US",
  ja: "ja-JP",
};

function safeLang(lang: string | undefined): Lang {
  return (LANGUAGES as readonly string[]).includes(lang ?? "") ? (lang as Lang) : "vi";
}

/** Format date theo locale hiện tại của react-i18next. */
export function formatDate(
  value: string | number | Date | null | undefined,
  lang: Lang
): string {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString(LOCALE_BY_LANG[lang]);
  } catch {
    return String(value);
  }
}

export function formatUiDateTime(
  value: string | number | Date | null | undefined,
  lang: Lang
): string {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat(LOCALE_BY_LANG[lang], {
      dateStyle: "short",
      timeStyle: "short",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

export function formatUiDateTimeWithSeconds(
  value: string | number | Date | null | undefined,
  lang: Lang
): string {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat(LOCALE_BY_LANG[lang], {
      dateStyle: "short",
      timeStyle: "medium",
    }).format(new Date(value));
  } catch {
    return String(value);
  }
}

export const formatDateTime = formatUiDateTime;

/** Hook tiện dụng — tự lấy lang từ i18n rồi formatDate. */
export function useFormatDate() {
  const { i18n } = useTranslation();
  const lang = safeLang(i18n.resolvedLanguage ?? i18n.language);
  return (value: string | number | Date | null | undefined) =>
    formatDate(value, lang);
}

export function useFormatUiDateTime() {
  const { i18n } = useTranslation();
  const lang = safeLang(i18n.resolvedLanguage ?? i18n.language);
  return (value: string | number | Date | null | undefined) =>
    formatUiDateTime(value, lang);
}

export function useFormatUiDateTimeWithSeconds() {
  const { i18n } = useTranslation();
  const lang = safeLang(i18n.resolvedLanguage ?? i18n.language);
  return (value: string | number | Date | null | undefined) =>
    formatUiDateTimeWithSeconds(value, lang);
}

export const useFormatDateTime = useFormatUiDateTime;

export function humanSize(n: number | null | undefined): string {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let x = n;
  while (x >= 1024 && i < u.length - 1) {
    x /= 1024;
    i++;
  }
  return x.toFixed(i && x < 10 ? 1 : 0) + " " + u[i];
}

export function formatGroupedNumber(
  value: number | string | null | undefined,
  options?: Intl.NumberFormatOptions
): string {
  if (value == null || value === "") return "—";
  const numeric =
    typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
    ...options,
  }).format(numeric);
}
